/**
 * Portage TypeScript du design AGORA (design/components/bundle.js, version du 21/10/2026).
 * Règle : même structure DOM et mêmes classes `ag-…` que la référence, pour que bundle.css s'applique tel quel.
 * Le code suit volontairement l'écriture `h(...)` du bundle pour faciliter la comparaison lors des mises à jour du design.
 * Aucun libellé en dur : tout texte visible est une prop fournie (déjà traduite) par l'appelant.
 */
import React from 'react';

export const h = React.createElement;

export function cx(...classes: unknown[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Ajout AGORA – zoom de l'affichage appliqué à la racine (--agx-zoom, web/src/core/zoom.ts). getBoundingClientRect et
 * clientX / clientY sont à l'échelle de l'écran, les longueurs CSS (style, offsetLeft…) à celle de la page : diviser
 * une mesure d'écran par ce facteur avant de l'écrire dans un style.
 */
export function zoomRacine(): number {
  const z = typeof document === 'undefined' ? 1 : parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--agx-zoom'));
  return z > 0 ? z : 1;
}

let compteur = 0;
/** Identifiant stable pour les liaisons label ↔ champ et les clipPath SVG. */
export function useIdStable(prefixe: string, fourni?: string): string {
  return React.useMemo(() => fourni || `${prefixe}${++compteur}`, [fourni, prefixe]);
}

export type Status = 'info' | 'progress' | 'waiting' | 'success' | 'danger' | 'neutral';
export type Metier = 'service' | 'etude' | 'commerce' | 'mes' | 'qhse' | 'achats' | 'finance' | 'it';

/* ---------- Icon ---------- */
const P = {
  home: 'M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z',
  calendar: 'M4 6h16v14H4zM4 10h16M8 3v4M16 3v4',
  wrench: 'M14.5 6.5a4 4 0 0 0 5 5L12 19a2.1 2.1 0 0 1-3-3l7.5-7.5zM14.5 6.5 17 4l3 3-2.5 2.5',
  ticket: 'M3 7h18v3a2 2 0 0 0 0 4v3H3v-3a2 2 0 0 0 0-4zM14 7v10',
  shield: 'M12 3 4 6v6c0 4.5 3.4 8 8 9 4.6-1 8-4.5 8-9V6zM9 12l2 2 4-4',
  truck: 'M3 6h11v10H3zM14 10h4l3 3v3h-7M7 19a2 2 0 1 0 0-.01M17 19a2 2 0 1 0 0-.01',
  scan: 'M4 8V5a1 1 0 0 1 1-1h3M16 4h3a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1h-3M8 20H5a1 1 0 0 1-1-1v-3M4 12h16',
  chart: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  box: 'M12 3 3 7.5v9L12 21l9-4.5v-9zM3 7.5 12 12l9-4.5M12 12v9',
  book: 'M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3zM5 17a3 3 0 0 1 3-3h11',
  plus: 'M12 5v14M5 12h14',
  minus: 'M6 12h12',
  check: 'M5 12.5 10 17l9-10',
  more: 'M5 12h.01M12 12h.01M19 12h.01',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM16 16l5 5',
  bell: 'M6 16V11a6 6 0 0 1 12 0v5l2 2H4zM10 21h4',
  globe: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18',
  building: 'M5 21V4h10v17M15 9h4v12M3 21h18M8 8h4M8 12h4M8 16h4',
  chevron: 'M8 10l4 4 4-4',
  sidebar: 'M4 4h16v16H4zM9 4v16M15 10l-2 2 2 2',
  spark: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9zM19 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z',
  layers: 'M9 3h11a1 1 0 0 1 1 1v11M6 6h11a1 1 0 0 1 1 1v13H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z',
  grid: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z',
  close: 'M6 6l12 12M18 6 6 18',
  send: 'M4 12 20 4l-6 16-3-7z',
  user: 'M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM4 21c1-4 4.5-6 8-6s7 2 8 6',
  clock: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 7v5l3 2',
  back: 'M19 12H5M11 6l-6 6 6 6',
  grip: 'M9 6h.01M15 6h.01M9 12h.01M15 12h.01M9 18h.01M15 18h.01',
  sortIcon: 'M7 4v16M3 16l4 4 4-4M17 20V4M13 8l4-4 4 4',
  thumb: 'M7 10v10H4V10zM7 10l4-7c1.5 0 2.5 1 2.2 2.6L12.6 9H19a2 2 0 0 1 2 2.3l-1.2 7A2 2 0 0 1 17.8 20H7',
  chat: 'M4 5h16v11H9l-5 4z',
  share: 'M18 8a3 3 0 1 0 0-.01M6 15a3 3 0 1 0 0-.01M18 22a3 3 0 1 0 0-.01M8.6 13.5l6.8-4M8.6 16.5l6.8 4',
  pin: 'M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21zM12 7a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z',
  // Punaise « épingler au dock » (tracé Lucide « pin », licence ISC – le design recommande Lucide hors jeu minimal)
  punaise: 'M12 17v5M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z',
  folder: 'M3 6a1 1 0 0 1 1-1h5l2 2h9a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z',
  upload: 'M12 16V4M7 9l5-5 5 5M4 16v4h16v-4',
  external: 'M14 4h6v6M20 4l-9 9M18 14v6H4V6h6',
  sync: 'M20 11a8 8 0 0 0-14.9-3M4 13a8 8 0 0 0 14.9 3M5 4v4h4M19 20v-4h-4',
  lock: 'M6 11h12v9H6zM8.5 11V8a3.5 3.5 0 0 1 7 0v3',
  mail: 'M3 6h18v12H3zM3 7l9 6 9-6',
  key: 'M8 14a4 4 0 1 1 3.5-6L21 8v3h-2v2h-3l-1.5-1.5A4 4 0 0 1 8 14zM7 10h.01',
  arrow: 'M5 12h14M13 6l6 6-6 6',
  filter: 'M4 5h16l-6 8v6l-4-2v-4z',
  settings: 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM12 2v3M12 19v3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M2 12h3M19 12h3M4.9 19.1 7 17M17 7l2.1-2.1',
  map: 'M9 4 3 6v14l6-2 6 2 6-2V4l-6 2zM9 4v14M15 6v14',
  list: 'M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01',
  edit: 'M4 20h4L19 9l-4-4L4 16zM13 7l4 4',
  trash: 'M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13',
  // Ajouts AGORA (messages éphémères) : information, alerte
  info: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 11v5M12 8h.01',
  alerte: 'M12 4 2.5 20h19zM12 10v4.5M12 17.5h.01',
  // Ajouts AGORA (outil Messagerie, tracés Lucide « flag », « reply », « reply-all », « forward », « paperclip », « tag »,
  // licence ISC) : drapeau de suivi, répondre, répondre à tous, transférer, pièce jointe, catégorie
  drapeau: 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22v-7',
  repondre: 'M9 17l-5-5 5-5M20 18v-2a4 4 0 0 0-4-4H4',
  repondreTous: 'M7 17l-5-5 5-5M12 17l-5-5 5-5M22 18v-2a4 4 0 0 0-4-4H7',
  transferer: 'M15 17l5-5-5-5M4 18v-2a4 4 0 0 1 4-4h12',
  trombone: 'M21.4 11.1l-9.2 9.2a6 6 0 0 1-8.5-8.5l9.2-9.2a4 4 0 0 1 5.7 5.7l-9.2 9.2a2 2 0 0 1-2.8-2.8l8.5-8.5',
  etiquette: 'M12 2H2v10l9.3 9.3a2.4 2.4 0 0 0 3.4 0l6.6-6.6a2.4 2.4 0 0 0 0-3.4zM7 7h.01',
  // Ajouts AGORA (métiers Achats et Finance) : panier, portefeuille
  panier: 'M2.5 4h2.6l2.3 11h10.9l2.2-7.5H6.2M9.5 19.5a1.5 1.5 0 1 0 0-.01M17.5 19.5a1.5 1.5 0 1 0 0-.01',
  portefeuille: 'M19 7V5a1 1 0 0 0-1-1H6a2 2 0 0 0 0 4h13a1 1 0 0 1 1 1v3M20 16v3a1 1 0 0 1-1 1H6a2 2 0 0 1-2-2V6M20 12h-4a2 2 0 0 0 0 4h4zM16 14h.01',
  // Ajouts AGORA (softphone) : téléphone, raccrocher, micro, micro coupé (tracés Lucide « phone », « phone-off »,
  // « mic », « mic-off », licence ISC), attente
  telephone: 'M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z',
  raccrocher: 'M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67M5.39 10.39a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91M22 2 2 22',
  micro: 'M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zM19 10v2a7 7 0 0 1-14 0v-2M12 19v3',
  microCoupe: 'M2 2l20 20M18.89 13.23A7.12 7.12 0 0 0 19 12v-2M5 10v2a7 7 0 0 0 12 5M15 9.34V5a3 3 0 0 0-5.68-1.33M9 9v3a3 3 0 0 0 5.12 2.12M12 19v3',
  pause: 'M8 5v14M16 5v14',
  // Ajout AGORA (reprise d'une animation, diaporama de la connexion) : tracé Lucide « play », licence ISC
  lecture: 'M6 3l14 9-14 9z',
  // Ajouts AGORA (texte mis en forme, RichTextField : tracés Lucide « bold », « italic », « underline », « list-ordered »,
  // « link », « remove-formatting », licence ISC) : gras, italique, souligné, liste numérotée, lien, effacer la mise en forme
  gras: 'M6 12h9a4 4 0 0 1 0 8H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h7a4 4 0 0 1 0 8',
  italique: 'M19 4h-9M14 20H5M15 4 9 20',
  souligne: 'M6 4v6a6 6 0 0 0 12 0V4M4 20h16',
  listeNumeros: 'M10 6h11M10 12h11M10 18h11M4 6h1v4M4 10h2M6 18H4c0-1 2-2 2-3s-1-1.5-2-1',
  lien: 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71',
  effacerFormat: 'M4 7V4h16v3M5 20h6M13 4 8 20M15 15l5 5M20 15l-5 5',
} as const;

export type IconName = keyof typeof P;

export interface IconProps { name: IconName; size?: number; label?: string; className?: string }

export function Icon(p: IconProps) {
  const s = p.size || 24;
  const sw = p.size && p.size < 18 ? 2.25 : 1.75;
  return h('svg', {
    className: cx('ag-icon', p.className), width: s, height: s, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor',
    strokeWidth: sw, strokeLinecap: 'round', strokeLinejoin: 'round',
    'aria-hidden': p.label ? undefined : true, role: p.label ? 'img' : undefined, 'aria-label': p.label,
  }, h('path', { d: P[p.name] || P.more }));
}

/* ---------- Button ---------- */
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'glass' | 'quiet' | 'danger';
  size?: 'sm' | 'md' | 'touch';
  icon?: IconName;
}

/** Ajout AGORA : transmet sa référence au bouton (déclencheur d'un menu ou d'une fenêtre Radix, focus initial). */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(p, ref) {
  const { variant, size, icon, className, children, ...rest } = p;
  const v = variant || 'secondary';
  const s = size || 'md';
  return h('button', {
    type: 'button', ...rest, ref,
    className: cx('ag-btn', 'ag-btn-' + v, s !== 'md' && 'ag-btn-' + s, v === 'glass' && 'ag-glass', className),
  }, icon ? h(Icon, { name: icon, size: s === 'touch' ? 22 : 18 }) : null, children);
});

/* ---------- StatusBadge ---------- */
const SG: Record<Status, React.ReactNode> = {
  info: h('circle', { cx: 6, cy: 6, r: 4.25, fill: 'none', stroke: 'currentColor', strokeWidth: 1.5 }),
  progress: h(React.Fragment, null,
    h('circle', { cx: 6, cy: 6, r: 4.25, fill: 'none', stroke: 'currentColor', strokeWidth: 1.5 }),
    h('path', { d: 'M6 1.75a4.25 4.25 0 0 1 0 8.5z', fill: 'currentColor' })),
  waiting: h('path', { d: 'M4.5 3v6M7.5 3v6', stroke: 'currentColor', strokeWidth: 1.75, strokeLinecap: 'round' }),
  success: h('path', { d: 'M2.5 6.25 5 8.75l4.5-5', fill: 'none', stroke: 'currentColor', strokeWidth: 1.75, strokeLinecap: 'round', strokeLinejoin: 'round' }),
  danger: h('path', { d: 'M6 1.5 11 10.5H1zM6 5v2.25M6 8.75v.01', fill: 'none', stroke: 'currentColor', strokeWidth: 1.4, strokeLinejoin: 'round', strokeLinecap: 'round' }),
  neutral: h('path', { d: 'M3 6h6', stroke: 'currentColor', strokeWidth: 1.75, strokeLinecap: 'round' }),
};

export interface StatusBadgeProps { status: Status; label: string }

export function StatusBadge(p: StatusBadgeProps) {
  const s: Status = SG[p.status] ? p.status : 'neutral';
  return h('span', { className: cx('ag-badge', 'ag-badge-' + s) },
    h('svg', { width: 12, height: 12, viewBox: '0 0 12 12', 'aria-hidden': true }, SG[s]), p.label);
}

/* ---------- PriorityTag ---------- */
export interface PriorityTagProps { level: 1 | 2 | 3 | 4; label?: string }

export function PriorityTag(p: PriorityTagProps) {
  const l = Math.min(4, Math.max(1, p.level || 3));
  return h('span', { className: cx('ag-prio', 'ag-prio-' + l) },
    h('span', { className: 'ag-prio-bars', 'aria-hidden': true }, h('i'), h('i'), h('i'), h('i')), p.label || 'P' + l);
}

/* ---------- ModuleTag ---------- */
export interface ModuleTagProps { metier: Metier; label: string }

export function ModuleTag(p: ModuleTagProps) {
  return h('span', { className: cx('ag-mod', 'ag-mod-' + p.metier) }, p.label);
}

/* ---------- KpiTile ---------- */
export interface KpiTileProps { label: string; value: string; unit?: string; delta?: string; tone?: 'good' | 'bad' | 'flat' }

export function KpiTile(p: KpiTileProps) {
  return h('div', { className: 'ag-kpi' },
    p.label ? h('div', { className: 'ag-kpi-label' }, p.label) : null,
    h('div', { className: 'ag-kpi-value' }, p.value, p.unit ? h('small', null, p.unit) : null),
    p.delta ? h('div', { className: cx('ag-kpi-delta', 'ag-kpi-' + (p.tone || 'flat')) }, p.delta) : null);
}

/* ---------- RecordCard ---------- */
export interface RecordCardProps {
  reference: string; title: string; status?: Status; statusLabel?: string; priority?: 1 | 2 | 3 | 4; priorityLabel?: string;
  metier?: Metier; moduleLabel?: string; meta?: { label: string; value: string }[]; children?: React.ReactNode;
}

export function RecordCard(p: RecordCardProps) {
  return h('article', { className: 'ag-card' },
    h('div', { className: 'ag-card-top' }, h('span', { className: 'ag-card-ref' }, p.reference),
      p.status ? h(StatusBadge, { status: p.status, label: p.statusLabel || '' }) : null),
    h('h4', { className: 'ag-card-title' }, p.title),
    p.meta && p.meta.length ? h('dl', { className: 'ag-card-meta' }, p.meta.map((m, i) =>
      h('div', { key: i }, h('dt', null, m.label), h('dd', null, m.value)))) : null,
    h('div', { className: 'ag-card-foot' },
      p.metier ? h(ModuleTag, { metier: p.metier, label: p.moduleLabel || '' }) : null,
      p.priority ? h(PriorityTag, { level: p.priority, label: p.priorityLabel }) : null, p.children));
}
